import { AlertTriangle, CheckCircle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export type ConfirmDialogVariant = "default" | "destructive" | "warning" | "success";

const variantStyles: Record<
  ConfirmDialogVariant,
  {
    circleBg: string;
    iconColor: string;
    buttonBg: string;
    buttonHover: string;
    Icon: React.ElementType;
  }
> = {
  default: {
    circleBg: "bg-brand-cyan/15",
    iconColor: "text-brand-cyan",
    buttonBg: "bg-brand-cyan",
    buttonHover: "hover:bg-brand-cyan/90",
    Icon: Info,
  },
  destructive: {
    circleBg: "bg-status-danger-subtle",
    iconColor: "text-status-danger-icon",
    buttonBg: "bg-status-danger",
    buttonHover: "hover:bg-status-danger-hover",
    Icon: AlertTriangle,
  },
  warning: {
    circleBg: "bg-status-warning-subtle",
    iconColor: "text-status-warning-icon",
    buttonBg: "bg-status-warning",
    buttonHover: "hover:bg-status-warning-hover",
    Icon: AlertTriangle,
  },
  success: {
    circleBg: "bg-status-success-subtle",
    iconColor: "text-status-success-icon",
    buttonBg: "bg-status-success",
    buttonHover: "hover:bg-status-success-hover",
    Icon: CheckCircle,
  },
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
  onConfirm: () => void;
}) {
  const { circleBg, iconColor, buttonBg, buttonHover, Icon } = variantStyles[variant];

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm flex flex-col items-center text-center gap-5 px-8 py-8">
        {/* Icon */}
        <div className={cn("flex items-center justify-center h-14 w-14 rounded-full", circleBg)}>
          <Icon className={cn("h-6 w-6", iconColor)} strokeWidth={2} />
        </div>

        {/* Title + Description */}
        <div className="flex flex-col gap-1.5">
          <DialogTitle className="text-base font-bold text-primary leading-snug">
            {title}
          </DialogTitle>
          {description ? (
            <DialogDescription className="text-sm font-normal text-muted/70 leading-relaxed">
              {description}
            </DialogDescription>
          ) : (
            <DialogDescription className="sr-only">Confirm this action.</DialogDescription>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 w-full">
          <DialogClose asChild>
            <Button variant="outline" size="default" className="flex-1 bg-surface">
              {cancelLabel}
            </Button>
          </DialogClose>
          <Button
            size="default"
            className={cn("flex-1 text-white", buttonBg, buttonHover)}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
